import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Header, Button, Dimmer, Message } from 'semantic-ui-react';
import { SimpleFormWrapper } from '../SimpleFormWrapper/SimpleFormWrapper';
import { Logo } from '../Logo/Logo';
import { LoginForm, LoginFormFields } from '../LoginForm/LoginForm';
import { Spinner } from '../Spinner/Spinner';
import {
	ChangePasswordFormFields,
	ChangePasswordForm,
} from '../ChangePasswordForm/ChangePasswordForm';
import { AppState } from '../../types/app-state';
import { AuthState } from '../../types/auth-state';
import { OrganisationState } from '../../types/organisation-state';
import { ForgotPasswordState } from '../../types/forgotpassword-state';
import { Logout } from '../../actions/auth.actions';
import { Reset, ChangePassword } from '../../actions/forgotpassword.actions';
import { PreselectOrganisation } from '../../actions/organisation.actions';
import { GetVerificationCode } from '../../actions/forgotpassword.actions';

export const ForgotPasswordPage: React.FC = () => {
	const dispatch = useDispatch();
	const { email: authEmail } = useSelector<AppState, Partial<AuthState>>(
		(state) => state.auth
	);
	const { preselectedOrg } = useSelector<AppState, Partial<OrganisationState>>(
		(state) => state.organisation
	);
	const { codeVerified, message, loading, token } = useSelector<
		AppState,
		Partial<ForgotPasswordState>
	>((state) => state.forgotpassword);

	useEffect(() => {
		dispatch(Logout());
		dispatch(Reset());
		const searchParams = new URLSearchParams(window.location.search);
		const tenantId = searchParams.get('tenantId');
		if (tenantId) {
			dispatch(PreselectOrganisation(tenantId));
		}
	}, [dispatch]);

	const handleSubmit = (values: LoginFormFields) => {
		dispatch(GetVerificationCode(values.email));
	};

	const handleChangePasswordSubmit = (values: ChangePasswordFormFields) => {
		dispatch(ChangePassword(values.password, values.code));
	};

	return (
		<div className='forgotpassword-page'>
			<Dimmer inverted active={loading}>
				<Spinner />
			</Dimmer>
			{!codeVerified && !token && (
				<SimpleFormWrapper>
					<Logo />
					{message && (
						<Message negative fluid>
							<p>{message}</p>
						</Message>
					)}
					<LoginForm
						defaultEmail={authEmail}
						onSubmit={handleSubmit}
						showPassword={false}
						renderActionButtons={() => (
							<Button fluid primary type='submit'>
								Submit
							</Button>
						)}
					>
						<Header as='h3'>
							{authEmail ? 'Change Password' : 'Forgot Password'}
						</Header>
						<p>
							Please enter your email. We will send you the verification code to
							your email.
						</p>
					</LoginForm>
				</SimpleFormWrapper>
			)}
			{!codeVerified && token && (
				<SimpleFormWrapper>
					<Logo />
					{message && (
						<Message negative fluid>
							<p>{message}</p>
						</Message>
					)}
					<ChangePasswordForm
						onSubmit={handleChangePasswordSubmit}
						renderActionButtons={() => (
							<Button fluid primary type='submit'>
								Submit
							</Button>
						)}
					>
						<Header as='h3'>Change your password</Header>
						<p>Please also provide the code you get from the email.</p>
					</ChangePasswordForm>
				</SimpleFormWrapper>
			)}
			{codeVerified && (
				<SimpleFormWrapper>
					<Logo />
					<p>Password has been updated</p>
					<Button
						as={Link}
						to={`/login${preselectedOrg ? '?tenantId=' + preselectedOrg : ''}`}
						fluid
						color='grey'
					>
						Click here to login
					</Button>
				</SimpleFormWrapper>
			)}
		</div>
	);
};

export default ForgotPasswordPage;
